import type { VoidFunctionComponent } from 'react';

import { formatCount, prettyNumber } from '../../../../shared/utils/numbers';
import Badge from '../Badge/Badge';

type Props = {
  /** The numeric count to display in the badge. Formatted via `formatCount` (e.g. "1.2k" for large values). */
  count: number;
  /** When true, shows "..." instead of the count. Use while the count is being fetched. */
  isLoading?: boolean;
  /** When true, shows "--" instead of the count. Use when the count could not be retrieved (e.g. the query failed). */
  isUnavailable?: boolean;
  /** Optional tooltip text shown on hover. If omitted, counts >= 10,000 fall back to the full, un-abbreviated number. */
  title?: string;
};

/**
 * Displays a count badge to the right of the children.
 * Optionally accepts an isLoading, which if true will display "..." instead of the count.
 * If the count is unavailable due to an error, it can display "--" instead.
 * Counts will be formatted as described in the formatCount function.
 */
const SubnavCountBadge: VoidFunctionComponent<Props> = ({ count, isLoading, isUnavailable, title }) => {
  //  Ensure that we never show NaN as the count
  const isFiniteCount = Number.isFinite(count);
  const finiteCount = isFiniteCount ? count : 0;
  let badgeTitle = title;
  if (!isUnavailable && !title && finiteCount >= 10_000) {
    badgeTitle = prettyNumber(finiteCount);
  }

  let badgeText = formatCount(finiteCount);
  if (isLoading || !isFiniteCount) {
    badgeText = '...';
  }
  if (isUnavailable) {
    badgeText = '--';
  }

  return (
    <Badge
      color="light-gray"
      shape="circle"
      size="micro"
      //  This will display the full count as a title if the count is >= 10,000
      title={badgeTitle}
    >
      {badgeText}
    </Badge>
  );
};

export default SubnavCountBadge;
